import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';

export interface OnboardingViewProps {
  onComplete: () => void;
}

interface OnboardingScreen {
  image: string;
  renderText: () => React.ReactNode;
}

export const ONBOARDING_SCREENS: OnboardingScreen[] = [
  {
    image: '/onboarding/onboarding_1.jpg',
    renderText: () => (
      <h1 className="text-2xl sm:text-4xl font-extrabold text-white text-center leading-tight tracking-tight">
        Buon Compleanno <span className="text-sky-400">Chiarettiiii</span>
      </h1>
    ),
  },
  {
    image: '/onboarding/onboarding_2.jpg',
    renderText: () => (
      <h2 className="text-xl sm:text-3xl font-bold text-white text-center leading-snug">
        Questo è il mio regalo per te
      </h2>
    ),
  },
  {
    image: '/onboarding/onboarding_3.jpg',
    renderText: () => (
      <p className="text-sm sm:text-base text-slate-200 text-center leading-relaxed max-w-lg font-normal">
        ho creato questa app per te, per dare un posto esaclusivo ai nostri ricordi, una casa per i
        nostri viaggi. è imperfetta, funziona male ed incompleta.. ma pian piano la sistemiamo ed
        arricchiamo. al mo momento ci trovi ancune foto che ti ho fatto nel nostro viaggio in
        islanda, pian piano le mettiamo tutte
      </p>
    ),
  },
];

export const OnboardingView: React.FC<OnboardingViewProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const touchStartX = useRef<number | null>(null);

  const handleNext = useCallback(() => {
    if (currentStep < ONBOARDING_SCREENS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete();
    }
  }, [currentStep, onComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Keyboard navigation (ArrowLeft / ArrowRight)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  // Touch Swipe Handlers for mobile navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX.current - touchEndX;

    if (diffX > 50) {
      // Swiped left -> next
      handleNext();
    } else if (diffX < -50) {
      // Swiped right -> prev
      handlePrev();
    }
    touchStartX.current = null;
  };

  const screen = ONBOARDING_SCREENS[currentStep];
  const isLast = currentStep === ONBOARDING_SCREENS.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950 text-slate-100 flex flex-col justify-between p-4 sm:p-8 select-none overflow-y-auto"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top Bar with Step Indicators */}
      <div className="max-w-md w-full mx-auto flex items-center justify-between pt-2">
        <span className="text-xs font-semibold text-slate-400">
          {currentStep + 1} di {ONBOARDING_SCREENS.length}
        </span>

        {/* Indicator dots */}
        <div className="flex items-center gap-2">
          {ONBOARDING_SCREENS.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setCurrentStep(idx)}
              aria-label={`Vai alla schermata ${idx + 1}`}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentStep ? 'w-8 bg-sky-400' : 'w-2 bg-slate-700 hover:bg-slate-600'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={onComplete}
          className="text-xs text-slate-400 hover:text-slate-200 transition cursor-pointer"
        >
          Salta
        </button>
      </div>

      {/* Main Content Area */}
      <div className="max-w-md sm:max-w-xl w-full mx-auto my-auto flex flex-col items-center gap-6 py-6 animate-fadeIn">
        {/* Photo Container */}
        <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] max-h-[52vh] rounded-3xl overflow-hidden bg-slate-900 shadow-2xl border border-slate-800 flex items-center justify-center">
          <img
            key={screen.image}
            src={screen.image}
            alt={`Foto ${currentStep + 1}`}
            className="w-full h-full object-cover transition-opacity duration-500"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent pointer-events-none" />
        </div>

        {/* Text Area */}
        <div className="w-full min-h-[90px] flex items-center justify-center px-2">
          {screen.renderText()}
        </div>
      </div>

      {/* Bottom Navigation Controls */}
      <div className="max-w-md w-full mx-auto flex items-center justify-between gap-4 pb-2">
        {currentStep > 0 ? (
          <button
            type="button"
            onClick={handlePrev}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 transition cursor-pointer active:scale-95 text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Indietro</span>
          </button>
        ) : (
          <div className="w-20" />
        )}

        <button
          type="button"
          onClick={handleNext}
          className="flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 text-white font-bold shadow-lg shadow-sky-500/25 transition cursor-pointer active:scale-95 text-sm sm:text-base ml-auto"
        >
          <span>{isLast ? "Entra nell'app" : 'Avanti'}</span>
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );
};
